import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations } from '../../configuration';
import { Button, Typography } from '@material-ui/core';
import {
  AppSelect,
  AppTextField,
  AppGroupText,
  AppDatePicker,
  AppCheckbox,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { useDispatch } from 'react-redux';
import RequestInformationActions from 'redux/reducers/request_reducer/actions';
import * as RequestInfoSelectors from 'redux/reducers/request_reducer/selectors';
import { SchemeTypes } from 'config';
import { StateStatus } from 'redux/utils/common';
import * as DateUtils from 'utils/dates';

interface IProps {}

interface FormTypes {
  schemeType: number | null;
  memberState: number | null;
  vatNumber?: string | null;
  intNumber?: string | null;
  vatTaxRef?: string | null;
  tradeDate?: Date | null;
  contractRelationship?: number | null;
  netpIntermediaryStatus?: string | null;
}

const defaultValues = {
  schemeType: null,
  memberState: null,
  vatNumber: null,
  intNumber: null,
  vatTaxRef: null,
  tradeDate: null,
  contractRelationship: null,
  netpIntermediaryStatus: null,
};

export default function RegistrationRequestForm(props: IProps): ReactElement {
  // console.log('Render RegistrationRequestForm');
  const classes = useStyles();
  const { errors, control, handleSubmit, reset, watch } = useForm<FormTypes>({
    defaultValues,
  });
  const dispatch = useDispatch();
  const status = RequestInfoSelectors.useGetRegistrationRequestStatus();
  const watchScheme = watch('schemeType');

  React.useEffect(() => {
    if (status === StateStatus.Success) {
      reset(defaultValues);
    }
  }, [status]);

  React.useEffect(() => {
    dispatch(RequestInformationActions.fetchMemberStateAction());
    dispatch(RequestInformationActions.fetchAllCountriesAction());
  }, [dispatch]);

  const onSubmit = (data: FormTypes) => {
    // console.log(data);
    dispatch(
      RequestInformationActions.registrationRequestAction({
        schemeType: data.schemeType as number,
        memberState: data.memberState as number,
        vatNumber: data.vatNumber as string,
        intNumber: data.intNumber as string,
        ...(data.tradeDate && {
          tradeDate: DateUtils.convertToUtc(data.tradeDate),
        }),
        // contractRelationship: data.vatNumber
        //   ? false
        //   : data.contractRelationship === 1
        //   ? true
        //   : data.contractRelationship === 2
        //   ? false
        //   : undefined,
        contractRelationship:
          data.contractRelationship === 1
            ? true
            : data.contractRelationship === 2
            ? false
            : undefined,
        vatTaxRef: data.vatTaxRef as string,
        netpIntermediaryStatus:
          data.netpIntermediaryStatus === null
            ? undefined
            : (data.netpIntermediaryStatus as string),
      })
    );
  };

  return (
    <div>
      <Typography variant="h5" style={{ marginBottom: '32px' }}>
        <TranslatedText defaultText={'Registration Request Information'} />
      </Typography>
      <form
        className={classes.form}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <GridLayout
          elements={[
            {
              element: renderSchemeType(errors, control),
              size: 6,
              id: 'scheme',
            },
            {
              element: renderMemberStateField(errors, control),
              size: 6,
              id: 'memberState',
            },
            {
              element: renderVatNumberField(
                errors,
                control,
                watchScheme === SchemeTypes.IOSS
                  ? 'Importer Number'
                  : 'VAT Number'
              ),
              size: watchScheme === SchemeTypes.IOSS ? 6 : 12,
              id: 'vatNumber',
            },
            ...(watchScheme === SchemeTypes.IOSS
              ? [
                  {
                    element: renderVatTaxRefField(errors, control),
                    size: 6,
                    id: 'vatTaxRefNumber',
                  } as any,
                  {
                    element: renderIntermediaryField(errors, control),
                    size: 6,
                    id: 'intermediary',
                  } as any,
                ]
              : []),
            ...(watchScheme === SchemeTypes.IOSS
              ? [
                  {
                    element: renderContractRelationship(errors, control),
                    size: 6,
                    id: 'contractRelationship',
                  } as any,
                ]
              : []),
            {
              element: renderTradeDateField(errors, control),
              size: 6,
              id: 'tradeDate',
            },

            {
              element: renderNetpIntermediaryStatus(errors, control),
              size: 6,
              id: 'netpIntermediaryStatus',
            } as any,

            {
              element: (
                <Button
                  className={classes.submitButton}
                  type="submit"
                  variant="contained"
                >
                  Submit Request
                </Button>
              ),
              size: 12,
              id: 'submit',
            },
          ]}
        />
      </form>
    </div>
  );
}

const renderSchemeType = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  const select_countries = [
    {
      id: SchemeTypes.EU,
      label: 'MOSS',
    },
    {
      id: SchemeTypes.NON_EU,
      label: 'VOES',
    },
    {
      id: SchemeTypes.IOSS,
      label: 'IMPORT',
    },
  ];

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Scheme Type'}
      id="schemeType"
      name="schemeType"
      required
      error={'schemeType' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.schemeType ? errors.schemeType.message : ' '}
    />
  );
};

const renderMemberStateField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  let countries = RequestInfoSelectors.useGetMemberStateCountries();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: 'Select member state country..',
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: `${country.code} - ${country.description}`,
      });

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Member State'}
      id="memberState"
      name="memberState"
      required
      error={'memberState' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.memberState ? errors.memberState.message : ' '}
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>,
  label: string
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={label}
      id="vatNumber"
      name="vatNumber"
      error={'vatNumber' in errors}
      control={control}
      rules={{}}
      helperText={errors.vatNumber ? errors.vatNumber.message : ' '}
    />
  );
};

const renderVatTaxRefField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={'VAT/Tax Reference Number'}
      id="vatTaxRef"
      name="vatTaxRef"
      error={'vatTaxRef' in errors}
      control={control}
      rules={{}}
      helperText={errors.vatTaxRef ? errors.vatTaxRef.message : ' '}
    />
  );
};

const renderIntermediaryField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppTextField
      variant="outlined"
      fullWidth
      label={'Intermediary Number'}
      id="intNumber"
      name="intNumber"
      error={'intNumber' in errors}
      control={control}
      rules={{}}
      helperText={errors.intNumber ? errors.intNumber.message : ' '}
    />
  );
};

const renderTradeDateField = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  return (
    <AppDatePicker
      fullWidth
      label={'Trade Date'}
      error={'tradeDate' in errors}
      control={control}
      rules={{}}
      autoOk
      name="tradeDate"
      helperText={errors.tradeDate && errors.tradeDate.message}
    />
  );
};

const renderContractRelationship = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  const contractRelationshipOptions = [
    {
      id: null,
      label: 'None',
    },
    {
      id: 1,
      label: 'True',
    },
    {
      id: 2,
      label: 'False',
    },
  ];

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Contract Relationship'}
      id="contractRelationship"
      name="contractRelationship"
      // required
      error={'contractRelationship' in errors}
      control={control}
      // rules={{ required: 'Required Field' }}
      rules={{}}
      options={contractRelationshipOptions as any}
      helperText={
        errors.contractRelationship ? errors.contractRelationship.message : ' '
      }
    />
  );
};

const renderNetpIntermediaryStatus = (
  errors: DeepMap<FormTypes, FieldError>,
  control: Control<FormTypes>
) => {
  const netpIntermediaryStatusOptions = [
    {
      id: null,
      label: 'None',
    },
    {
      id: 'ACTIVE',
      label: 'Active',
    },
    {
      id: 'EXCLUDED',
      label: 'Excluded',
    },
    {
      id: 'BOTH',
      label: 'Both',
    },
  ];

  return (
    <AppSelect
      variant="outlined"
      fullWidth
      label={'Registration Status'}
      id="netpIntermediaryStatus"
      name="netpIntermediaryStatus"
      // required
      error={'netpIntermediaryStatus' in errors}
      control={control}
      // rules={{ required: 'Required Field' }}
      rules={{}}
      options={netpIntermediaryStatusOptions as any}
      helperText={
        errors.netpIntermediaryStatus
          ? errors.netpIntermediaryStatus.message
          : ' '
      }
    />
  );
};
